/**
 * Which sign-in methods the backend allows right now (`GET /auth/policy`).
 *
 * Pure, framework-free logic only (no Vue, no axios) so it can be checked in
 * isolation. The backend enforces the policy; the screens only render it.
 */
export interface AuthPolicy {
    passwordLoginAllowed: boolean;
    /** ISO-8601 instant, or null when no cut-over is scheduled. */
    passwordLoginRetiresAt: string | null;
    easilyLoginAllowed: boolean;
    easilyLoginRetiresAt: string | null;
    magicLinkAvailable: boolean;
    passkeyAvailable: boolean;
    passwordSignupAllowed: boolean;
    passwordlessSignupAvailable: boolean;
    /** Username + password entry for accounts that have no email address. */
    noEmailPasswordLoginAllowed: boolean;
    noEmailPasswordLoginRetiresAt: string | null;
}

/**
 * Today's behaviour (password login only). Used when the backend has no
 * `/auth/policy` yet (404), the call fails, or it does not answer in time.
 */
export const LEGACY_POLICY: Readonly<AuthPolicy> = Object.freeze({
    passwordLoginAllowed: true,
    passwordLoginRetiresAt: null,
    easilyLoginAllowed: true,
    easilyLoginRetiresAt: null,
    magicLinkAvailable: false,
    passkeyAvailable: false,
    passwordSignupAllowed: true,
    passwordlessSignupAvailable: false,
    noEmailPasswordLoginAllowed: false,
    noEmailPasswordLoginRetiresAt: null,
});

export function legacyPolicy(): AuthPolicy {
    return {...LEGACY_POLICY};
}

type BoolKey = {
    [K in keyof AuthPolicy]: AuthPolicy[K] extends boolean ? K : never
}[keyof AuthPolicy];
type InstantKey = {
    [K in keyof AuthPolicy]: AuthPolicy[K] extends boolean ? never : K
}[keyof AuthPolicy];

/**
 * Strict parse of the `/auth/policy` payload. A field that is not a real
 * boolean takes the legacy value, so a malformed response can never hide the
 * password form by accident.
 */
export function parseAuthPolicy(raw: unknown): AuthPolicy {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return legacyPolicy();
    const r = raw as Record<string, unknown>;
    const bool = (k: BoolKey): boolean => {
        const v = r[k];
        return typeof v === "boolean" ? v : LEGACY_POLICY[k];
    };
    const instant = (k: InstantKey): string | null => {
        const v = r[k];
        return typeof v === "string" && !Number.isNaN(Date.parse(v)) ? v : null;
    };
    const policy: AuthPolicy = {
        passwordLoginAllowed: bool("passwordLoginAllowed"),
        passwordLoginRetiresAt: instant("passwordLoginRetiresAt"),
        easilyLoginAllowed: bool("easilyLoginAllowed"),
        easilyLoginRetiresAt: instant("easilyLoginRetiresAt"),
        magicLinkAvailable: bool("magicLinkAvailable"),
        passkeyAvailable: bool("passkeyAvailable"),
        passwordSignupAllowed: bool("passwordSignupAllowed"),
        passwordlessSignupAvailable: bool("passwordlessSignupAvailable"),
        noEmailPasswordLoginAllowed: bool("noEmailPasswordLoginAllowed"),
        noEmailPasswordLoginRetiresAt: instant("noEmailPasswordLoginRetiresAt"),
    };
    // Never leave the user without a way in (misconfigured backend).
    if (!policy.passwordLoginAllowed && !policy.magicLinkAvailable) {
        policy.passwordLoginAllowed = true;
    }
    return policy;
}

/** What the login screen renders. */
export interface LoginScreen {
    /** "ログイン用のメールを送る" is the primary action. */
    magicLink: boolean;
    /** Password form is shown at all. */
    password: boolean;
    /** Password form is the only/primary action — today's screen. */
    passwordPrimary: boolean;
    /** When set, show the "パスワードでのログインは終了します" notice. */
    passwordRetiresAt: string | null;
    /** Low-key "メールアドレスが登録されていない方" link. */
    noEmailEntry: boolean;
    /**
     * "指紋・顔でログイン" is offered (backend side). Independent of the magic
     * link; the screen also requires browser support before showing it.
     */
    passkey: boolean;
}

export function loginScreen(policy: AuthPolicy): LoginScreen {
    const password = policy.passwordLoginAllowed;
    return {
        magicLink: policy.magicLinkAvailable,
        password,
        passwordPrimary: password && !policy.magicLinkAvailable,
        passwordRetiresAt: password ? policy.passwordLoginRetiresAt : null,
        noEmailEntry: policy.magicLinkAvailable && policy.noEmailPasswordLoginAllowed,
        passkey: policy.passkeyAvailable,
    };
}

/** Same shape check as the backend's MailAddresses.normalize. */
export function isEmailLike(value: string): boolean {
    const v = value.trim();
    return v.length > 0 && v.length <= 100 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);
}

export const MSG_MISSING_EMAIL = "メールアドレスを入力してください。";
export const MSG_INVALID_EMAIL = "メールアドレスの形式が正しくありません。";

/** Inline message for an email field, or "" when it may be sent. */
export function emailFieldError(value: string, required: boolean): string {
    const v = value.trim();
    if (!v) return required ? MSG_MISSING_EMAIL : "";
    return isEmailLike(v) ? "" : MSG_INVALID_EMAIL;
}

/**
 * The email for the admin account-creation forms: required while the magic
 * link is available, optional otherwise. Returns the value to send, or
 * undefined to leave the field out of the request (today's request body).
 */
export function accountEmailToSend(value: string): string | undefined {
    const v = value.trim();
    return v ? v : undefined;
}

/**
 * Same-site path only. Rejects protocol-relative (`//`, `/\`), schemes and
 * control characters (browsers strip tab/newline, which could hide a scheme).
 */
export function safeRedirect(raw: string | null | undefined, fallback = "/"): string {
    if (!raw) return fallback;
    if (!raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return fallback;
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001f\u007f]/.test(raw)) return fallback;
    return raw;
}

/** "2027年1月31日" in Japan time, whatever the viewer's time zone. */
export function formatRetireDate(iso: string | null | undefined): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat("ja-JP", {
        timeZone: "Asia/Tokyo",
        year: "numeric",
        month: "long",
        day: "numeric",
    }).format(d);
}

export function passwordRetireNotice(iso: string | null | undefined): string {
    const date = formatRetireDate(iso);
    return date ? `${date}で、パスワードでのログインは終了します。これからは「メールでログイン」をお使いください。` : "";
}

/** `token` from a URL fragment such as `#token=abc` (or `token=abc`). */
export function tokenFromHash(hash: string): string {
    const h = hash.startsWith("#") ? hash.slice(1) : hash;
    if (!h) return "";
    return new URLSearchParams(h).get("token") || "";
}

/** Show the "メールアドレスを設定してください" notice for a signed-in user? */
export function shouldShowMailNotice(
    policy: AuthPolicy | null,
    user: { userId?: string | null; email?: string | null } | null | undefined,
): boolean {
    if (!policy || !policy.magicLinkAvailable || !user) return false;
    if (!user.userId) return false; // not loaded yet
    return !user.email || !String(user.email).trim();
}

/** Backend `AuthErrorResponse` (`{ errorCode, status, message, reason }`). */
export interface AuthError {
    status: number;
    reason: string;
    message: string;
}

/**
 * Turn an axios-style rejection into an AuthError. The backend `message` is
 * shown only when it is an AuthErrorResponse (has a `reason`) — other bodies
 * (e.g. framework defaults) may not be user-facing Japanese.
 */
export function toAuthError(e: unknown, fallbackMessage: string): AuthError {
    const err = (e && typeof e === "object" ? e : {}) as { response?: { status?: unknown; data?: unknown } };
    const res = err.response;
    const status = res && typeof res.status === "number" ? res.status : 0;
    const data = (res && res.data && typeof res.data === "object" ? res.data : {}) as Record<string, unknown>;
    const reason = typeof data.reason === "string" ? data.reason : "";
    const message = reason && typeof data.message === "string" && data.message.trim() ? data.message : fallbackMessage;
    return {status, reason, message};
}
